import { useSearchParams } from "react-router";
import { competenciaAtual, competenciaSchema } from "@bookdental/schemas";
import { useSetPageHeader } from "@/lib/page-header";
import { useAuth } from "@/lib/auth";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { FINANCEIRO_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { usePermissao } from "@/lib/use-permissao";
import { useFeature } from "@/lib/use-feature";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { SeletorDeMes } from "@/components/financeiro/SeletorDeMes";
import { PlanoPagoFinanceiro } from "@/components/financeiro/PlanoPagoFinanceiro";
import { ListaDeLancamentos } from "@/components/financeiro/lancamentos/ListaDeLancamentos";
import { VisaoDoMes } from "@/components/financeiro/visao/VisaoDoMes";
import { AbaDeCobrancas } from "@/components/cobrancas/AbaDeCobrancas";
import { ConfiguracoesDaCobranca } from "@/components/cobrancas/ConfiguracoesDaCobranca";
import { APagar } from "@/components/financeiro/a-pagar/APagar";
import { NotasFiscais } from "@/components/financeiro/notas/NotasFiscais";
import { FechamentoDosMeses } from "@/components/financeiro/config/FechamentoDosMeses";
import { MesFechado } from "@/components/financeiro/MesFechado";
import { CategoriasFinanceiras } from "@/components/financeiro/config/CategoriasFinanceiras";

const ABAS = [
  { chave: "visao", rotulo: "Visão do mês" },
  { chave: "receber", rotulo: "Cobranças" },
  { chave: "pagar", rotulo: "Contas a pagar" },
  { chave: "lancamentos", rotulo: "Lançamentos" },
  { chave: "notas", rotulo: "Notas fiscais" },
  { chave: "configuracoes", rotulo: "Configurações" },
] as const;
type Aba = (typeof ABAS)[number]["chave"];

export function Financeiro() {
  useSetPageHeader({ title: "Financeiro", subtitle: "Entradas, saídas e resultado do laboratório" }, []);
  // O servidor só atende laboratório: numa clínica (endereço digitado) as consultas falhavam e a tela
  // ficava no esqueleto pra sempre.
  const ehLab = !!useAuth().user?.activeOrganization?.types.includes("DENTAL_LAB");
  const temRecurso = useFeature("financeiro") && ehLab;
  const podeVer = usePermissao("finance.view");
  const [params, setParams] = useSearchParams();
  // Com os extratos ligados, Cobranças não é por mês do calendário: o seletor sai dessa aba.
  const extratosLigados = trpc.financeiro.configExtrato.ler.useQuery(undefined, { enabled: temRecurso && podeVer }).data?.ativo;

  const mesDaUrl = params.get("mes");
  const competencia = mesDaUrl && competenciaSchema.safeParse(mesDaUrl).success ? mesDaUrl : competenciaAtual();
  const abaDaUrl = params.get("aba");
  const aba: Aba = ABAS.some((a) => a.chave === abaDaUrl) ? (abaDaUrl as Aba) : "visao";
  const mudar = (chave: "mes" | "aba", valor: string) =>
    setParams((p) => {
      p.set(chave, valor);
      return p;
    }, { replace: true });
  const semMes = aba === "configuracoes" || (aba === "receber" && !!extratosLigados);
  // Sem seletor de mês na aba, o passo dele ficaria sem alvo e encerraria o tour.
  useTourDaPagina(temRecurso && podeVer ? (semMes ? FINANCEIRO_TOUR_STEPS.slice(0, 1) : FINANCEIRO_TOUR_STEPS) : []);

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4 pb-10">
      {!ehLab ? (
        <NoAccessBlock description="Por enquanto, o financeiro existe só para laboratórios." />
      ) : !temRecurso ? (
        <PlanoPagoFinanceiro />
      ) : !podeVer ? (
        <NoAccessBlock description="O financeiro é visível para o proprietário, os sócios e quem tiver a permissão." />
      ) : (
        <>
          <div className="flex flex-col-reverse gap-3 border-b border-zinc-200 sm:flex-row sm:items-end sm:justify-between dark:border-border">
            <div data-tour="financeiro-abas" className="flex gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Seções do financeiro">
              {ABAS.map((t) => (
                <button
                  key={t.chave}
                  type="button"
                  role="tab"
                  aria-selected={aba === t.chave}
                  onClick={() => mudar("aba", t.chave)}
                  className={cn(
                    "-mb-px whitespace-nowrap border-b-2 px-3 py-3 text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
                    aba === t.chave
                      ? "border-brand-600 font-semibold text-brand-700 dark:border-primary dark:text-primary"
                      : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground dark:hover:text-foreground",
                  )}
                >
                  {t.rotulo}
                </button>
              ))}
            </div>
            {!semMes && (
              <div data-tour="financeiro-mes" className="flex items-center gap-2 pb-2">
                <MesFechado competencia={competencia} />
                <SeletorDeMes valor={competencia} onChange={(m) => mudar("mes", m)} />
              </div>
            )}
          </div>

          {aba === "visao" && <VisaoDoMes competencia={competencia} onIr={(a) => mudar("aba", a)} />}
          {aba === "receber" && <AbaDeCobrancas competencia={competencia} onConfigurar={() => mudar("aba", "configuracoes")} />}
          {aba === "pagar" && <APagar competencia={competencia} />}
          {aba === "lancamentos" && <ListaDeLancamentos competencia={competencia} />}
          {aba === "notas" && <NotasFiscais competencia={competencia} />}
          {aba === "configuracoes" && (
            <div className="space-y-4">
              <ConfiguracoesDaCobranca />
              <FechamentoDosMeses />
              <CategoriasFinanceiras />
            </div>
          )}
        </>
      )}
    </div>
  );
}
